"use client";

import rehypeRaw from "rehype-raw";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import { ExpandOutlined } from "@ant-design/icons";
import remarkGfm from "remark-gfm";
import { type FC, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { SlidePreviewEnlargeModal, type SlidePreviewSlide } from "@/components/SlidePreviewEnlargeModal";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";

const MARKDOWN_IMAGE_ACTION_CHIP =
  "inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-[#d9dde3] bg-white px-3 text-[13px] font-medium leading-none text-[#1f2328] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:border-[#cdd3db] hover:bg-[#f8f9fb] hover:shadow-[0_2px_6px_rgba(0,0,0,0.10)] active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-1";

const MARKDOWN_IMAGE_ACTION_ICON_WRAP = "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[#1f2328]";

const MarkdownImage: FC<React.ImgHTMLAttributes<HTMLImageElement>> = ({ src, alt, className, ...props }) => {
  const [activeSlide, setActiveSlide] = useState<SlidePreviewSlide | null>(null);
  const slideSrc = src ?? "";
  const slideTitle = alt?.trim() || "Image preview";
  const slide = slideSrc ? ({ id: slideSrc, title: slideTitle, src: slideSrc } satisfies SlidePreviewSlide) : null;
  const slides = slide ? [slide] : [];

  return (
    <>
      <div className="group relative w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50">
        <div className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100" />
        <div className="pointer-events-none absolute bottom-3 left-3 z-[2] flex gap-2 opacity-0 transition-[opacity,transform] duration-[160ms] ease-out [transform:translateY(4px)] group-hover:pointer-events-auto group-hover:opacity-100 group-hover:[transform:translateY(0)]">
          <button
            type="button"
            className={cn(MARKDOWN_IMAGE_ACTION_CHIP, "cursor-pointer")}
            aria-label={`Enlarge preview of ${slideTitle}`}
            onClick={() => slide && setActiveSlide(slide)}
            disabled={!slide}
          >
            <span className={MARKDOWN_IMAGE_ACTION_ICON_WRAP}>
              <ExpandOutlined className="!block !h-3.5 !w-3.5" />
            </span>
            Enlarge
          </button>
        </div>
        <img
          src={slideSrc}
          alt={alt ?? ""}
          className={cn("block h-full w-full object-contain", className)}
          {...props}
        />
      </div>

      <SlidePreviewEnlargeModal
        open={activeSlide != null}
        onClose={() => setActiveSlide(null)}
        slides={slides}
        activeSlide={activeSlide}
        onActiveSlideChange={setActiveSlide}
        headerTitle="Preview"
        footerVariant="closeOnly"
      />
    </>
  );
};

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      remarkRehypeOptions={{ allowDangerousHtml: true }}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-t-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white">
      <span className="lowercase [&>span]:text-xs">{language}</span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1 className={cn("mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0", className)} {...props} />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn("mb-4 mt-8 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn("mb-4 mt-6 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn("mb-4 mt-6 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0", className)}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5 className={cn("my-4 text-lg font-semibold first:mt-0 last:mb-0", className)} {...props} />
  ),
  h6: ({ className, ...props }) => (
    <h6 className={cn("my-4 font-semibold first:mt-0 last:mb-0", className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p className={cn("mb-1 mt-1 leading-7 first:mt-0 last:mb-0", className)} {...props} />
  ),
  br: ({ ...props }) => <br className="mb-1" {...props} />,
  img: MarkdownImage,
  a: ({ className, ...props }) => (
    <a className={cn("text-primary font-medium underline underline-offset-4", className)} {...props} />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote className={cn("border-l-2 pl-6 italic", className)} {...props} />
  ),
  ul: ({ className, ...props }) => <ul className={cn("my-5 ml-6 list-disc [&>li]:mt-2", className)} {...props} />,
  ol: ({ className, ...props }) => <ol className={cn("my-5 ml-6 list-decimal [&>li]:mt-2", className)} {...props} />,
  hr: ({ className, ...props }) => <hr className={cn("my-5 border-b", className)} {...props} />,
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "border-separate border-spacing-0 overflow-y-auto text-xs leading-snug [&_strong]:text-sm [&_strong]:font-semibold",
        className
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "bg-muted px-2 py-1 text-left text-xs font-semibold leading-snug first:rounded-tl-lg last:rounded-tr-lg [&[align=center]]:text-center [&[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "border-b border-l px-2 py-1 text-left text-xs font-normal leading-snug last:border-r [&[align=center]]:text-center [&[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => <sup className={cn("[&>a]:text-xs [&>a]:no-underline", className)} {...props} />,
  pre: ({ className, ...props }) => (
    <pre className={cn("overflow-x-auto rounded-b-lg bg-black p-4 text-white", className)} {...props} />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return <code className={cn(!isCodeBlock && "bg-muted rounded border font-semibold", className)} {...props} />;
  },
  CodeHeader,
});
